import { ACTIVE_EDIT_VIEWS } from '../../constants';
import {
  selectMostRecentlyUpdatedField,
  selectVAPContactInfoField,
  selectVAPServiceTransaction,
  selectVAProfilePersonalInformation,
  selectVAProfileSchedulingPreferences,
} from '../../selectors';
import { isFailedTransaction } from '../transactions';
import { hasError } from './fieldControllerPredicates';

/**
 * A field's value, wherever it lives. Contact info, personal information and
 * scheduling preferences are three different slices of the profile.
 */
export const selectFieldData = (state, fieldName) =>
  selectVAPContactInfoField(state, fieldName) ||
  selectVAProfilePersonalInformation(state, fieldName) ||
  selectVAProfileSchedulingPreferences(state, fieldName);

export const shouldShowUpdateSuccessAlert = (state, fieldName) => {
  const mostRecentSaveField = selectMostRecentlyUpdatedField(state);

  return Array.isArray(mostRecentSaveField)
    ? mostRecentSaveField.includes(fieldName)
    : mostRecentSaveField === fieldName;
};

export const shouldShowErrorAlert = (state, fieldName) => {
  const { transaction, transactionRequest } = selectVAPServiceTransaction(
    state,
    fieldName,
  );

  return hasError(transaction, transactionRequest);
};

/**
 * Which of the field's views is showing, derived from the single
 * `vapService.modal` value that identifies the currently open edit view.
 */
export const deriveModalFlags = ({
  fieldName,
  activeEditView,
  addressValidationType,
  hasUnsavedEdits,
}) => {
  const isValidatingThisField =
    addressValidationType === fieldName &&
    activeEditView === ACTIVE_EDIT_VIEWS.ADDRESS_VALIDATION;

  return {
    /*
      This ternary is to deal with an edge case: if the user is currently viewing
      the address validation view we need to handle things differently or text in
      the modal would be inaccurate. This is an unfortunate hack to get around an
      existing hack we've been using to determine if we need to show the address
      validation view or not.
      */
    activeEditView:
      activeEditView === ACTIVE_EDIT_VIEWS.ADDRESS_VALIDATION
        ? addressValidationType
        : activeEditView,
    blockEditMode: !!(activeEditView && hasUnsavedEdits),
    showEditView: activeEditView === fieldName,
    showRemoveModal: activeEditView === `remove-${fieldName}`,
    showValidationView: !!isValidatingThisField,
  };
};

/**
 * The uiSchema with a caller-supplied title swapped in. Only phone and email
 * fields nest their title, so everything else is returned untouched.
 */
export const buildCustomUiSchema = (uiSchema, title) => {
  if (!title) {
    return uiSchema;
  }

  if (uiSchema?.inputPhoneNumber?.['ui:title']) {
    return {
      ...uiSchema,
      inputPhoneNumber: {
        ...uiSchema.inputPhoneNumber,
        'ui:title': title,
      },
    };
  }

  if (uiSchema?.emailAddress?.['ui:title']) {
    return {
      ...uiSchema,
      emailAddress: {
        ...uiSchema.emailAddress,
        'ui:title': title,
      },
    };
  }

  return uiSchema;
};

export const deriveTransactionError = (transaction, transactionRequest) => {
  if (transactionRequest?.error) {
    return transactionRequest.error;
  }

  // A failed transaction with no request-level error still needs to render the
  // generic error message, so it gets an empty error object.
  return isFailedTransaction(transaction) ? {} : null;
};
